import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { DOSE_BASIS, toOption } from "pages/MasterPengobatanStandar/constants";
import { errorMessages } from "services/StandardTreatment";

export const REGIMEN_MODAL_ID = "regimenFormModal";

const BASIS_OPTIONS = DOSE_BASIS.map((b) => ({ label: b === "WEIGHT" ? "WEIGHT — berat badan" : b, value: b }));
const STATUS_OPTIONS = [
  { label: "Aktif", value: true },
  { label: "Nonaktif", value: false },
];
const EMPTY = { drug_id: "", code: "", name: "", dose_basis: "WEIGHT", is_active: true };
const TITLES = { add: "Tambah Aturan Dosis", edit: "Edit Aturan Dosis", detail: "Detail Aturan Dosis" };

const DrugSummary = ({ drug }) =>
  drug ? (
    <dl className="md-detail md-highlight-box mt-2">
      <dt>Kode</dt>
      <dd>{drug.code}</dd>
      <dt>Sediaan</dt>
      <dd>{drug.dosage_form || "—"}</dd>
      <dt>Medication key</dt>
      <dd>{drug.medication_key ? <code className="md-code">{drug.medication_key}</code> : "Belum dipetakan"}</dd>
    </dl>
  ) : null;

// RegimenCreate butuh drug_id; RegimenUpdate tidak punya drug_id → obat terkunci saat edit.
const RegimenFormModal = ({ mode, regimen, drugs, onSubmit, onEdit }) => {
  const history = useHistory();
  const [serverErrors, setServerErrors] = useState([]);
  const isEdit = mode === "edit";
  const drugOptions = drugs
    .filter((d) => d.is_active || d.id === regimen?.drug_id)
    .map((d) => ({ label: `${d.name} (${d.code})${d.is_active ? "" : " — nonaktif"}`, value: d.id }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: regimen ? { ...EMPTY, ...regimen } : EMPTY,
    validationSchema: Yup.object({
      drug_id: Yup.string().required("Pilih obat"),
      code: Yup.string().trim().max(50).required("Kode wajib diisi"),
      name: Yup.string().trim().max(150).required("Nama aturan dosis wajib diisi"),
    }),
    onSubmit: (v) => {
      setServerErrors([]);
      const body = { code: v.code.trim(), name: v.name.trim(), dose_basis: v.dose_basis, is_active: v.is_active };
      return onSubmit(isEdit ? body : { drug_id: v.drug_id, ...body }).catch((err) => setServerErrors(errorMessages(err)));
    },
  });

  const errorOf = (key) => formik.touched[key] && formik.errors[key];
  const selectedDrug = drugs.find((d) => d.id === formik.values.drug_id);

  return (
    <div className="modal fade md-modal" id={REGIMEN_MODAL_ID} tabIndex={-1} aria-labelledby={`${REGIMEN_MODAL_ID}Label`} aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${REGIMEN_MODAL_ID}Label`}>
              {TITLES[mode]}
            </h5>
            <button type="button" className="close" data-dismiss="modal" aria-label="Tutup">
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {mode === "detail" && regimen ? (
            <>
              <div className="modal-body">
                <dl className="md-detail">
                  <dt>Obat</dt>
                  <dd>{regimen.drug?.name || "—"}</dd>
                  <dt>Kode</dt>
                  <dd>{regimen.code}</dd>
                  <dt>Nama</dt>
                  <dd>{regimen.name}</dd>
                  <dt>Dose basis</dt>
                  <dd>{regimen.dose_basis}</dd>
                  <dt>Status</dt>
                  <dd>
                    <span className={`mps-pill ${regimen.is_active ? "is-success" : "is-neutral"}`}>
                      {regimen.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </dd>
                  <dt>Dosis standar</dt>
                  <dd>
                    {/* data-dismiss bereaksi sebelum react-router → navigasi tak jalan; paksa push. */}
                    <Link
                      to={`/dosis-standar?regimen_id=${regimen.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        history.push(`/dosis-standar?regimen_id=${regimen.id}`);
                      }}
                    >
                      Kelola dosis standar aturan dosis ini
                    </Link>
                  </dd>
                </dl>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
                  Tutup
                </Button>
                <Button type="button" variant="primary" size="normal" onClick={onEdit}>
                  Edit
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="modal-body">
                {serverErrors.length > 0 && (
                  <div className="mps-alert is-error" role="alert">
                    <ul className="mps-alert-list mt-0">
                      {serverErrors.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="md-field">
                  <InputSelect
                    label="Obat"
                    isRequired
                    isClearable={false}
                    disabled={isEdit}
                    placeholder="Pilih dari Master Obat"
                    data={drugOptions}
                    value={toOption(drugOptions, formik.values.drug_id)}
                    onChange={(sel) => formik.setFieldValue("drug_id", sel?.value ?? "")}
                    error={errorOf("drug_id")}
                    errorMessage={formik.errors.drug_id}
                  />
                  {isEdit && <small className="mps-helper">Obat tidak dapat diganti setelah aturan dosis dibuat.</small>}
                  <DrugSummary drug={selectedDrug} />
                </div>
                <div className="row">
                  <div className="col-sm-5 md-field">
                    <InputField
                      label="Kode"
                      isRequired
                      placeholder="Contoh: PQ-FALCIPARUM"
                      {...formik.getFieldProps("code")}
                      error={errorOf("code")}
                      errorMessage={formik.errors.code}
                    />
                  </div>
                  <div className="col-sm-7 md-field">
                    <InputField
                      label="Nama aturan dosis"
                      isRequired
                      placeholder="Contoh: Primaquine dosis tunggal Falciparum"
                      {...formik.getFieldProps("name")}
                      error={errorOf("name")}
                      errorMessage={formik.errors.name}
                    />
                  </div>
                  <div className="col-sm-6 md-field">
                    <InputSelect
                      label="Dose basis"
                      isClearable={false}
                      data={BASIS_OPTIONS}
                      value={toOption(BASIS_OPTIONS, formik.values.dose_basis)}
                      onChange={(sel) => formik.setFieldValue("dose_basis", sel?.value ?? "WEIGHT")}
                    />
                  </div>
                  <div className="col-sm-6 md-field">
                    <InputSelect
                      label="Status"
                      isClearable={false}
                      data={STATUS_OPTIONS}
                      value={toOption(STATUS_OPTIONS, formik.values.is_active)}
                      onChange={(sel) => formik.setFieldValue("is_active", sel?.value ?? true)}
                    />
                  </div>
                </div>
                {!formik.values.is_active && (
                  <p className="md-warning">
                    Aturan dosis nonaktif tidak ditawarkan pada Terapi, dan versi yang masih memakainya
                    ditolak saat rilis (REFERENCED_MASTER_ACTIVE).
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
                  Batal
                </Button>
                <Button type="submit" variant="primary" size="normal" isDisabled={formik.isSubmitting}>
                  {formik.isSubmitting ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegimenFormModal;
